// MarketBackdrop.jsx
// DAS-2832: what the market around this holding is doing — the top-down half of an
// alert's context, next to the bottom-up company blocks.
//
// The scoring already decides something specific about each alert ("this buy collides
// with the rising Fed-policy-error risk"), and that decision moves the urgency number.
// Until DAS-2831 it survived only inside the urgency hover text, mixed in with
// arithmetic, so it was effectively invisible. This block says it in one sentence.
//
// Four lines, deliberately: the matched evidence, the wind on this sector, the two
// horizon-appropriate readings, and a link out. Anything longer belongs in the Broad
// Picture panel — the context panel is already long.
import React, { useEffect, useState } from 'react';
import { Typography, Box, Stack, Divider, Link as MuiLink } from '@mui/material';
import { Tag, Tooltip } from 'antd';
import MarketWeatherBadge from './MarketWeatherBadge';   // DAS-2765
import { fetchMarketBackdrop, windForArea } from '../functions/fetchMarketBackdrop';

// DAS-2801: antd tooltips sit at z-index 1070 and a MUI Dialog at 1300, so a hover
// inside a modal renders BEHIND it. This block is rendered inside the alert modal.
const TIP_Z = 2000;

// Mirrors HORIZON_COLOR in BroadPicture.jsx so the two screens read as one system.
const HORIZON_COLOR = {
  hours: 'red', days: 'volcano', 'days-weeks': 'orange',
  weeks: 'gold', 'weeks-months': 'blue', months: 'green',
};

const DIRECTION = {
  rising: { arrow: '↑', color: 'volcano', word: 'building' },
  stable: { arrow: '→', color: 'blue', word: 'unchanged' },
  easing: { arrow: '↓', color: 'green', word: 'fading' },
  obsolete: { arrow: '✕', color: 'default', word: 'resolved' },
};

const AXIS_LABEL = { growth: 'Growth', inflation: 'Inflation' };

// "3 of 5 signals" beats a bare percentage — the percentage is the share of voting
// weight that landed on the winning state, which reads as a probability and isn't one.
const axisStrength = (a) => {
  if (a?.no_signal) return 'no clear signal';
  const fired = a?.n_fired;
  const total = a?.n_signals;
  if (fired != null && total != null) return `${fired} of ${total} signals`;
  return null;
};

/**
 * @param alert  the alert row — carries market_fit_* from DAS-2831
 * @param sector the holding's sector, for the wind line
 */
export default function MarketBackdrop({ alert, sector }) {
  const [backdrop, setBackdrop] = useState(null);

  const source = alert?.market_fit_source || null;

  useEffect(() => {
    let alive = true;
    if (!source) return undefined;           // nothing matched → nothing to fetch
    fetchMarketBackdrop().then((d) => { if (alive) setBackdrop(d); });
    return () => { alive = false; };
  }, [source]);

  // Nothing in the Broad Picture matched this holding, so there is nothing honest to
  // say. Hiding beats a generic line repeated on every card (agreed on DAS-2832).
  if (!source) return null;

  const isSell = (alert?.alert_type || '').toLowerCase() === 'harvest';
  const sideWord = isSell ? 'sell' : 'buy';
  const supports = Number(alert?.market_fit_alignment) > 0;
  const verdict = supports ? `supports this ${sideWord}` : `works against this ${sideWord}`;
  const name = alert?.market_fit_name || '';
  const risk = (backdrop?.risks || {})[alert?.market_fit_slug] || null;
  const dir = risk?.direction ? DIRECTION[risk.direction] : null;

  // Line 1 — what matched, in plain words. The wording follows the same six tiers the
  // score uses, so the sentence and the number can never tell different stories.
  const headline = (() => {
    switch (source) {
      case 'risk_sector':
      case 'risk_region':
        return {
          lead: `${dir ? `${dir.arrow} ` : ''}${name}`,
          tail: `${verdict}${source === 'risk_region' ? ' (matched on its region)' : ''}`,
          color: supports ? 'green' : 'volcano',
        };
      case 'alert_sector':
        return { lead: name, tail: `${verdict} (a market alert on this sector)`,
                 color: supports ? 'green' : 'volcano' };
      case 'wind_sector':
      case 'wind_region': {
        // A buy is helped by a tailwind and a sell by a headwind, so the stored
        // +1/-1 plus the alert's side tells us which kind of wind this was.
        const kind = (supports !== isSell) ? 'tailwind' : 'headwind';
        const where = source === 'wind_region' ? 'region' : 'sector';
        return { lead: name, tail: `${verdict} (a ${kind} on this ${where})`,
                 color: supports ? 'green' : 'orange' };
      }
      case 'lean':
        return { lead: name === 'risk-off' ? 'Market leaning risk-off' : 'Market leaning risk-on',
                 tail: `${verdict} — nothing specific to this holding matched`,
                 color: supports ? 'green' : 'orange' };
      default:
        return { lead: name, tail: verdict, color: 'default' };
    }
  })();

  const why = [
    risk?.why || '',
    risk?.tier ? `How seriously to take it: ${risk.tier}.` : '',
    (risk?.sources || []).length ? `Sources: ${risk.sources.join(' · ')}` : '',
    risk?.description || '',
  ].filter(Boolean).join('\n\n');

  // Line 2 — the wind on this sector, unless line 1 already IS that wind.
  const wind = (source === 'wind_sector' || source === 'wind_region')
    ? null
    : windForArea(backdrop, 'sector', sector);

  const axes = backdrop?.axes || {};

  return (
    <Box data-testid="market-backdrop" sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Market backdrop
        <Tooltip
          zIndex={TIP_Z}
          overlayStyle={{ maxWidth: 420 }}
          title={(
            <span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
              {'What the market around this holding is doing — as opposed to the blocks below, '
               + 'which are about the company itself.\n\n'
               + 'The first line is the piece of evidence that actually moved this alert\'s '
               + 'urgency score. Growth and inflation are shown because they stay valid for '
               + 'weeks to months, about as long as an alert decision lasts.'}
            </span>
          )}
        >
          <span style={{ marginLeft: 6, cursor: 'help', opacity: 0.55, fontSize: 13 }}>ⓘ</span>
        </Tooltip>
      </Typography>

      {/* 1) the matched evidence */}
      <Box data-testid="market-backdrop-match" sx={{ mb: 0.75 }}>
        <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 420 }}
                 title={why ? <span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{why}</span> : ''}>
          <Tag color={headline.color}
               style={{ whiteSpace: 'normal', cursor: why ? 'help' : 'default', marginRight: 8 }}>
            {headline.lead}
          </Tag>
        </Tooltip>
        {risk?.tier && <Tag style={{ fontSize: 10, marginRight: 8 }}>{risk.tier}</Tag>}
        <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
          {headline.tail}
        </Typography>
      </Box>

      {/* 2) the wind on this sector */}
      {wind && (
        <Box sx={{ mb: 0.75 }}>
          <Typography component="span" variant="body2">
            {wind.kind === 'tailwind' ? 'Tailwind' : 'Headwind'} on {sector}
            {/* The wind's own label is usually just the sector name — only add it
                when it actually says something more ("Energy producers / shipping"). */}
            {wind.area && wind.area !== sector ? `: ${wind.area}` : ''}
          </Typography>
          {wind.horizon && (
            <Tag color={HORIZON_COLOR[wind.horizon] || 'default'} style={{ marginLeft: 8 }}>
              ⏱{wind.horizon}
            </Tag>
          )}
        </Box>
      )}

      {/* 3) the two horizon-appropriate readings + today's weather */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', mb: 0.75 }}>
        {Object.keys(AXIS_LABEL).map((ax) => {
          const a = axes[ax];
          if (!a?.state) return null;
          const strength = axisStrength(a);
          return (
            <Typography key={ax} component="span" variant="body2" color="text.secondary">
              {AXIS_LABEL[ax]}: <b>{a.no_signal ? 'no clear signal' : a.state}</b>
              {strength && !a.no_signal ? ` (${strength})` : ''}
            </Typography>
          );
        })}
        <MarketWeatherBadge />
      </Stack>

      {/* 4) the way out — the full picture lives in its own panel */}
      <MuiLink
        component="button"
        type="button"
        variant="body2"
        data-testid="market-backdrop-link"
        onClick={() => {
          if (typeof window !== 'undefined' && typeof window.openToolsMenu === 'function') {
            window.openToolsMenu('broad_picture');
          }
        }}
      >
        → See the Broad Picture
      </MuiLink>

      <Divider sx={{ mt: 2, mb: 2 }} />
    </Box>
  );
}
